import { Player, PlayerWithExtra, ExtraData, FilterConfig } from './types';

export function parseExtraData(extraData: string | null): ExtraData {
  if (!extraData) return {};

  try {
    return JSON.parse(extraData);
  } catch (error) {
    console.error('Failed to parse extra_data:', error);
    return {};
  }
}

export function enrichPlayersWithExtraData(players: Player[]): PlayerWithExtra[] {
  return players.map(player => ({
    ...player,
    // Normalize registered to number to avoid type coercion issues
    registered: Number(player.registered),
    parsedExtraData: parseExtraData(player.extra_data),
  }));
}

export function applyFilters(players: PlayerWithExtra[], filters: FilterConfig[]): PlayerWithExtra[] {
  if (filters.length === 0) return players;

  return players.filter(player => {
    return filters.every(filter => {
      const rawValue = getPlayerFieldValue(player, filter.field);
      const value = String(rawValue).toLowerCase();
      const filterValue = filter.value.toLowerCase();

      switch (filter.operator) {
        case 'equals':
          if (filter.type === 'boolean' && (filterValue === 'true' || filterValue === 'false')) {
            return normalizeBooleanFilterValue(rawValue) === filterValue;
          }
          return value === filterValue;
        case 'contains':
          return value.includes(filterValue);
        case 'startsWith':
          return value.startsWith(filterValue);
        case 'endsWith':
          return value.endsWith(filterValue);
        default:
          return true;
      }
    });
  });
}

function normalizeBooleanFilterValue(value: string | number | boolean): 'true' | 'false' | '' {
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'number') {
    return value === 1 ? 'true' : value === 0 ? 'false' : '';
  }

  const normalized = value.trim().toLowerCase();

  if (['true', '1', 'yes', 'y'].includes(normalized)) {
    return 'true';
  }

  if (['false', '0', 'no', 'n', ''].includes(normalized)) {
    return 'false';
  }

  return '';
}

export function getPlayerFieldValue(player: PlayerWithExtra, field: string): string | number | boolean {
  // Check if it's a main field
  if (field in player && field !== 'parsedExtraData' && field !== 'extra_data') {
    return player[field as keyof Player] as string | number;
  }

  // Check in extra data
  if (field in player.parsedExtraData) {
    return player.parsedExtraData[field];
  }

  return '';
}

export function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

export function isTrueBooleanValue(value: unknown): boolean {
  return (
    value === true ||
    value === 1 ||
    value === '1' ||
    (typeof value === 'string' && ['true', 'yes'].includes(value.trim().toLowerCase()))
  );
}

export function formatTeeTime(teetime: string | null): string {
  if (!teetime) return '-';
  return teetime.substring(0, 5);
}

export function getAllExtraDataKeys(players: PlayerWithExtra[]): string[] {
  const keysSet = new Set<string>();

  players.forEach(player => {
    Object.keys(player.parsedExtraData).forEach(key => keysSet.add(key));
  });

  return Array.from(keysSet).sort();
}
